/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React from 'react';
import { Collapse, Input } from 'reactstrap';
import Select, { createFilter, Props } from 'react-select';
import { DashboardSection, DashboardSectionProps } from '../dashboard';
import IconButton from '../button/IconButton';
import { SelectOption } from '../../types/select';
import './style.scss';

export interface SidebarProps {
  children: JSX.Element | JSX.Element[];
  className?: string;
}

interface FilterOptionProps<
  Option,
  IsMulti extends boolean = false
> extends Props<Option, IsMulti> {
  disabled?: boolean;
  tooltip?: string;
  title?: string;
  subtitle?: string;
}

export function FilterOption<
  Option = SelectOption,
  IsMulti extends boolean = false
>(props: FilterOptionProps<Option, IsMulti>) {
  const {
    disabled = false,
    tooltip,
    title,
    subtitle,
    placeholder,
    isClearable,
    options,
    value,
    onChange,
    isMulti,
    closeMenuOnSelect,
    styles
  } = props;

  if (disabled) {
    return <></>
  }
  const showTopRow = title || tooltip || subtitle;

  const filterOption = createFilter<Option>({
    stringify: option => `${(option.data as SelectOption).searchString ?? option.label}`
  })

  return (
    <>
      {showTopRow ?
        <div className='mt-2'>
          <div className='text-ThemeAccentExtradark d-flex align-items-center'>
            {title ? <div>{title}</div> : null}
            {tooltip ? <div className='ml-auto'><InfoButton tooltip={tooltip} /></div> : null}
          </div>
          {subtitle ? <div className='text-ThemeAccentExtradark mt-1 mb-2'>{subtitle}</div> : null}
        </div>
        : <></>
      }

      <div>
        <Select<Option, IsMulti>
          isMulti={isMulti}
          menuPlacement='auto'
          placeholder={placeholder}
          isClearable={isClearable}
          options={options}
          value={value}
          onChange={(o, v) => onChange?.(o, v)}
          filterOption={filterOption}
          closeMenuOnSelect={closeMenuOnSelect}
          className={'mt-2 text-nowrap'}
          classNamePrefix={'Select'}
          styles={styles}
        />
      </div>
    </>
  )
}

export const InfoButton = ({ tooltip }: { tooltip?: JSX.Element | string }) => {
  if (!tooltip) {
    return <></>;
  }
  return <IconButton tooltip={tooltip} icon='fa-info' />
}

export const SettingsSection = (props: DashboardSectionProps) => {
  const { children, title, subtitle, icon, isVisible, className, headingStyle = 5 } = props;

  const [isOpen, setOpen] = React.useState<boolean | undefined>(undefined);

  if (isVisible === false) {
    return <></>;
  }

  let buttons = props.buttons ? [...props.buttons] : [];
  buttons.push({
    tooltip: isOpen ? 'Open' : 'Close',
    icon: isOpen ? 'fa-chevron-up' : 'fa-chevron-down',
    onClick: () => setOpen(!isOpen),
    className: 'd-xxl-none ml-2'
  });

  const collapseClassName = isOpen !== true ? 'd-none d-xxl-block' : '';

  return (
    <DashboardSection
      title={title}
      icon={icon}
      buttons={buttons}
      subtitle={<span className={collapseClassName}>{subtitle}</span>}
      padding={0}
      headingStyle={headingStyle}
      className={className}
    >
      <Collapse className={collapseClassName} isOpen={isOpen} >
        {children}
      </Collapse>
    </DashboardSection>
  );
}

interface SearchBoxOptionsInterface {
  value: string;
  subtitle?: string;
  placeholder?: string;
  handleOnChange: (e: React.FormEvent<HTMLInputElement>) => void;
}

export const SearchBox = (props: SearchBoxOptionsInterface) => {
  return (
    <div className='mt-2'>
      {props.subtitle ? <div className='text-ThemeAccentExtradark'>
        {props.subtitle}
      </div> : <></>}
      <Input placeholder={props.placeholder ?? 'Search'} onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.handleOnChange(e)} value={String(props.value)} />
    </div>
  );
}

export default function SettingsSidebar(props: SidebarProps) {
  return <div className={`settings-sidebar-container ${props.className ?? ''}`}>
    <div className='sticky-sidebar'>
      {props.children}
    </div>
  </div>
}
